/**
 * 5. geçiş — akıllı yumuşatma (à-trous, tam çözünürlük).
 *
 * Gücü pikselin geçmiş uzunluğuna bağlı: yeni açılan yer (n = 1) tam yumuşatılır,
 * n büyüdükçe azalır, BLUR_FADE_FRAMES kareden sonra hiç dokunulmaz.
 * Böylece gürültü görünmez ve birikim bitince ayrıntı geri gelir.
 *
 * Her tur 3×3 komşu okur; turlar arasında aralık ikiye katlanır (à-trous).
 * Başka yüzeydeki komşu sayılmaz (screenSurface.surfaceSimilarity).
 */

import { Fn, If, float, mix, uv, vec2, vec4 } from 'three/tsl';
import { readSurface, surfaceSimilarity } from './screenSurface.js';

const BLUR_FADE_FRAMES = 8;
const KERNEL = [ 0.25, 0.5, 0.25 ];

/**
 * @param {Object} node - SparseGiV2Node
 * @param {TextureNode} inputTexture - bu turun okuduğu doku
 * @param {UniformNode<float>} stepPixels - komşu aralığı (piksel); stride değişince node günceller
 */
export function buildDenoisePass( node, inputTexture, stepPixels ) {

	return Fn( () => {

		const screenUv = uv();
		const centerValue = inputTexture.sample( screenUv ).toVar();
		const historyLength = node._historyInfoCurrent.sample( screenUv ).x;
		const blurAmount = float( 1 ).sub( historyLength.sub( 1 ).div( BLUR_FADE_FRAMES ).saturate() );

		const result = centerValue.toVar();

		If( blurAmount.greaterThan( 0.001 ), () => {

			const center = readSurface( node, screenUv );

			If( center.isSky.not(), () => {

				const texelStep = vec2( stepPixels ).div( node._fullResolution );
				const sum = centerValue.mul( KERNEL[ 1 ] * KERNEL[ 1 ] ).toVar();
				const weightSum = float( KERNEL[ 1 ] * KERNEL[ 1 ] ).toVar();

				for ( let dy = - 1; dy <= 1; dy ++ ) {

					for ( let dx = - 1; dx <= 1; dx ++ ) {

						if ( dx === 0 && dy === 0 ) continue;

						const tapUv = screenUv.add( vec2( dx, dy ).mul( texelStep ) );
						const weight = surfaceSimilarity( center, readSurface( node, tapUv ) ).mul( KERNEL[ dx + 1 ] * KERNEL[ dy + 1 ] );

						sum.addAssign( inputTexture.sample( tapUv ).mul( weight ) );
						weightSum.addAssign( weight );

					}

				}

				result.assign( mix( centerValue, sum.div( weightSum ), blurAmount ) );

			} );

		} );

		return result;

	} )();

}

/** Tur başına komşu aralığı: stride 4 → 2, 4, 8 piksel. */
export function denoiseStepPixels( stride, passIndex ) {

	return Math.max( 1, stride / 2 ) * Math.pow( 2, passIndex );

}
